import type { Candle, Indicators } from "./types";

export function sma(values: number[], period: number): number | null {
  if (values.length < period) return null;
  const slice = values.slice(-period);
  return slice.reduce((sum, value) => sum + value, 0) / period;
}

export function ema(values: number[], period: number): number | null {
  if (values.length < period) return null;
  const multiplier = 2 / (period + 1);
  let average = sma(values.slice(0, period), period);
  if (average === null) return null;
  for (const value of values.slice(period)) {
    average = value * multiplier + average * (1 - multiplier);
  }
  return average;
}

export function rsi(values: number[], period = 14): number | null {
  if (values.length <= period) return null;
  let gains = 0;
  let losses = 0;
  for (let index = values.length - period; index < values.length; index += 1) {
    const change = values[index] - values[index - 1];
    if (change >= 0) gains += change;
    else losses -= change;
  }
  if (losses === 0) return 100;
  const relativeStrength = gains / period / (losses / period);
  return 100 - 100 / (1 + relativeStrength);
}

export function macd(values: number[], fast = 12, slow = 26, signalPeriod = 9): Indicators["macd"] {
  if (values.length < slow + signalPeriod) return null;
  const line: number[] = [];
  for (let index = slow; index <= values.length; index += 1) {
    const range = values.slice(0, index);
    const fastEma = ema(range, fast);
    const slowEma = ema(range, slow);
    if (fastEma !== null && slowEma !== null) line.push(fastEma - slowEma);
  }
  const signal = ema(line, signalPeriod);
  const latest = line.at(-1);
  if (signal === null || latest === undefined) return null;
  return { macd: latest, signal, histogram: latest - signal };
}

export function bollingerBands(values: number[], period = 20, deviations = 2): Indicators["bollinger"] {
  const middle = sma(values, period);
  if (middle === null) return null;
  const slice = values.slice(-period);
  const variance = slice.reduce((sum, value) => sum + (value - middle) ** 2, 0) / period;
  const standardDeviation = Math.sqrt(variance);
  return {
    upper: middle + standardDeviation * deviations,
    middle,
    lower: middle - standardDeviation * deviations
  };
}

export function atr(candles: Candle[], period = 14): number | null {
  if (candles.length <= period) return null;
  const trueRanges = candles.slice(1).map((candle, index) => {
    const previousClose = candles[index].close;
    return Math.max(
      candle.high - candle.low,
      Math.abs(candle.high - previousClose),
      Math.abs(candle.low - previousClose)
    );
  });
  return sma(trueRanges, period);
}

export function vwap(candles: Candle[], period = 24): number | null {
  if (candles.length < period) return null;
  const slice = candles.slice(-period);
  const volumePrice = slice.reduce((sum, candle) => {
    const typical = (candle.high + candle.low + candle.close) / 3;
    return sum + typical * candle.volume;
  }, 0);
  const volume = slice.reduce((sum, candle) => sum + candle.volume, 0);
  return volume === 0 ? null : volumePrice / volume;
}

function supportResistance(candles: Candle[], period = 72): Indicators["structure"] {
  if (candles.length < period) return null;
  const slice = candles.slice(-period);
  return {
    support: Math.min(...slice.map((candle) => candle.low)),
    resistance: Math.max(...slice.map((candle) => candle.high))
  };
}

function volumeProfile(candles: Candle[], period = 24): Indicators["volume"] {
  if (candles.length < period * 2) return null;
  const recent = candles.slice(-period);
  const previous = candles.slice(-(period * 2), -period);
  const recentVolume = sma(recent.map((candle) => candle.volume), period);
  const previousVolume = sma(previous.map((candle) => candle.volume), period);
  if (recentVolume === null || previousVolume === null) return null;
  return {
    recentVolume,
    previousVolume,
    expansion: previousVolume > 0 ? recentVolume / previousVolume : 1
  };
}

function candlePattern(candles: Candle[]): string {
  if (candles.length < 3) return "neutral";
  const [a, b, c] = candles.slice(-3);
  const body = Math.abs(c.close - c.open);
  const range = c.high - c.low || 1;
  const upperWick = c.high - Math.max(c.open, c.close);
  const lowerWick = Math.min(c.open, c.close) - c.low;
  if (b.close < b.open && c.close > c.open && c.close > b.open && c.open < b.close) return "bullish engulfing";
  if (b.close > b.open && c.close < c.open && c.open > b.close && c.close < b.open) return "bearish engulfing";
  if (lowerWick > body * 2 && upperWick < body) return "hammer";
  if (upperWick > body * 2 && lowerWick < body) return "shooting star";
  if (a.close < b.close && b.close < c.close) return "three-candle advance";
  if (a.close > b.close && b.close > c.close) return "three-candle decline";
  if (body / range < 0.12) return "doji";
  return "neutral";
}

function historicalProbability(candles: Candle[], horizon = 6): number {
  if (candles.length < 80) return 50;
  const closes = candles.map((candle) => candle.close);
  const currentRsi = rsi(closes, 14);
  const currentMomentum = (closes.at(-1) ?? 0) - (closes.at(-10) ?? 0);
  if (currentRsi === null) return 50;

  let matches = 0;
  let successes = 0;
  for (let index = 40; index < candles.length - horizon - 1; index += 1) {
    const pastCloses = closes.slice(0, index + 1);
    const pastRsi = rsi(pastCloses, 14);
    const pastMomentum = pastCloses.at(-1)! - pastCloses.at(-10)!;
    if (pastRsi === null) continue;
    const similarRsi = Math.abs(pastRsi - currentRsi) <= 7;
    const similarMomentum = Math.sign(pastMomentum) === Math.sign(currentMomentum);
    if (!similarRsi || !similarMomentum) continue;
    matches += 1;
    const futureReturn = (closes[index + horizon] - closes[index]) / closes[index];
    if (Math.sign(futureReturn) === Math.sign(currentMomentum || 1)) successes += 1;
  }
  if (matches < 5) return 50;
  return Math.round((successes / matches) * 100);
}

export function analyzeIndicators(candles: Candle[]): Indicators {
  const closes = candles.map((candle) => candle.close);
  const close = closes.at(-1) ?? 0;
  const sma20 = sma(closes, 20);
  const sma50 = sma(closes, 50);
  const ema12 = ema(closes, 12);
  const ema26 = ema(closes, 26);
  const momentum = close - (closes.at(-10) ?? close);
  const trend = sma20 !== null && sma50 !== null && close > sma20 && sma20 > sma50
    ? "bullish"
    : sma20 !== null && sma50 !== null && close < sma20 && sma20 < sma50
      ? "bearish"
      : "sideways";

  return {
    close,
    sma20,
    sma50,
    ema12,
    ema26,
    rsi14: rsi(closes, 14),
    macd: macd(closes),
    bollinger: bollingerBands(closes),
    atr14: atr(candles),
    vwap24: vwap(candles),
    structure: supportResistance(candles),
    volume: volumeProfile(candles),
    candlePattern: candlePattern(candles),
    trend,
    momentum,
    historicalProbability: historicalProbability(candles)
  };
}
